import { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios'
import qs from 'qs'

import { Api } from 'common/api/api'
import { apiConfig } from 'common/api/api.config'
import { INSTANCE_ID } from 'common/api/storageConsts'

import { MainFolder } from 'common/types/home'
import { PaginatedResponse } from 'common/types/managers'
import {
  ZipParams,
  DownloadParams,
  CopyOrMoveData,
  FileOperationResponse,
  AsyncOperationStatus,
  CopyAppToWorkspaceData,
  CopyAppToTeamData
} from 'common/types/fileManager'
import { UploaderParams } from 'store/socket/constants'

const FILE_MANAGER = '/fileManager'
// Matches DEFAULT_PAGE_SIZE in pyplan-api filemanager/service.py.
export const FILE_MANAGER_PAGE_SIZE = 50
// Page size used when we still need the FULL listing in one logical call (tree
// children, legacy callers). We page through in chunks of this size.
const FULL_LISTING_CHUNK = 500

export interface FolderPageParams {
  folder: string
  page?: number
  page_size?: number
  order_by?: string
  // Show hidden files/folders (maps to the backend `apply_filters` flag).
  apply_filters?: boolean
  // FilterOptionsIds selected (extension filters). Sent comma-separated.
  extensions?: string[]
  only_folders?: boolean
}

export interface SearchPageParams {
  text: string
  folder_path: string
  hidden?: boolean
  page?: number
  page_size?: number
  order_by?: string
  extensions?: string[]
  only_folders?: boolean
}

export class FileManagerApi extends Api {
  public constructor(config: AxiosRequestConfig) {
    super(config)

    // this middleware is been called right before the http request is made.
    this.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param
    }))

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }))

    Object.setPrototypeOf(this, FileManagerApi.prototype)
    this.getMainFolders = this.getMainFolders.bind(this)
    this.downloadFileOrFolder = this.downloadFileOrFolder.bind(this)
    this.prepareDownloadFileOrFolder = this.prepareDownloadFileOrFolder.bind(this)
  }

  private buildBrowserDownloadUrl(params: URLSearchParams): string {
    const baseUrl = String(apiConfig.baseURL || '').replace(/\/$/, '')
    const downloadPath = `${FILE_MANAGER}/download/`
    const authToken = sessionStorage.getItem('authToken')
    const instanceId = sessionStorage.getItem(INSTANCE_ID)

    if (authToken) {
      params.set('auth_token', authToken)
      params.set('session_key', 'browser-download')
    }

    if (instanceId) {
      params.set('instanceId', instanceId)
    }

    if (baseUrl) {
      return `${baseUrl}${downloadPath}?${params.toString()}`
    }

    return `${downloadPath}?${params.toString()}`
  }

  public async getWorkspaceAndPublic(): Promise<MainFolder[]> {
    try {
      const res: AxiosResponse<MainFolder[]> = await this.get(
        `${FILE_MANAGER}/getWorkspaceAndPublic/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   *
   * @param {string} path -Location of the folder.
   * @description Get size of a folder.
   */
  public async getFolderSize(path: string): Promise<{ result: string }> {
    try {
      const query = `?folder=${encodeURIComponent(path)}`
      const res: AxiosResponse<{ result: string }> = await this.get(
        `${FILE_MANAGER}/getFolderSize/${query}`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   *
   * @param {string} text -File or folder name to search.
   * @description Search a file or folder.
   */
  private buildListingQuery(params: {
    page?: number
    page_size?: number
    order_by?: string
    apply_filters?: boolean
    hidden?: boolean
    extensions?: string[]
    only_folders?: boolean
    folder?: string
    folder_path?: string
    text?: string
  }): string {
    const search = new URLSearchParams()
    if (params.folder != null) search.set('folder', params.folder)
    if (params.folder_path != null) search.set('folder_path', params.folder_path)
    if (params.text != null) search.set('text', params.text)
    if (params.page != null) search.set('page', String(params.page))
    if (params.page_size != null) search.set('page_size', String(params.page_size))
    if (params.order_by) search.set('order_by', params.order_by)
    if (params.apply_filters) search.set('apply_filters', 'true')
    if (params.hidden) search.set('hidden', 'true')
    if (params.only_folders) search.set('only_folders', 'true')
    if (params.extensions && params.extensions.length > 0) {
      search.set('extensions', params.extensions.join(','))
    }
    return search.toString()
  }

  /**
   * Paginated page of a folder's contents (folders first). Backend response
   * shape: { results, count, total_pages }. Used by the useFolderContents hook.
   */
  public async getFolderPage(params: FolderPageParams): Promise<PaginatedResponse<MainFolder>> {
    const query = this.buildListingQuery(params)
    const res: AxiosResponse<PaginatedResponse<MainFolder>> = await this.get(
      `${FILE_MANAGER}/getFiles/?${query}`
    )
    return this.success(res)
  }

  /**
   * Paginated page of search results (recursive) with the same shape.
   */
  public async searchPage(params: SearchPageParams): Promise<PaginatedResponse<MainFolder>> {
    const query = this.buildListingQuery(params)
    const res: AxiosResponse<PaginatedResponse<MainFolder>> = await this.get(
      `${FILE_MANAGER}/search/?${query}`
    )
    return this.success(res)
  }

  private async fetchAllPages(params: FolderPageParams): Promise<MainFolder[]> {
    let page = 1
    let totalPages = 1
    let all: MainFolder[] = []
    do {
      const res = await this.getFolderPage({
        ...params,
        page,
        page_size: FULL_LISTING_CHUNK
      })
      all = all.concat(res.results ?? [])
      totalPages = res.total_pages ?? 1
      page += 1
    } while (page <= totalPages)
    return all
  }

  /**
   *
   * @param {string} text -File or folder name to search.
   * @description Search a file or folder. Returns the full (paged-through) list
   * for legacy callers; use searchPage for incremental loading.
   */
  public async search(text: string, folder_path: string, hidden?: boolean): Promise<MainFolder[]> {
    let page = 1
    let totalPages = 1
    let all: MainFolder[] = []
    do {
      const res = await this.searchPage({
        text,
        folder_path,
        hidden,
        page,
        page_size: FULL_LISTING_CHUNK
      })
      all = all.concat(res.results ?? [])
      totalPages = res.total_pages ?? 1
      page += 1
    } while (page <= totalPages)
    return all
  }

  /**
   * Folders-only children of a folder (for the navigation tree). Paged through
   * so the full set is returned even for folders with many subfolders.
   */
  public async getFolderChildren(folder: string): Promise<MainFolder[]> {
    return this.fetchAllPages({ folder, only_folders: true, apply_filters: true })
  }

  /**
   * Returns which of the given names already exist under target. Used to decide
   * whether to prompt for overwrite on paste (the client no longer holds the
   * full folder listing in memory once the listing is paginated).
   */
  public async checkExists(target: string, names: string[]): Promise<string[]> {
    const res: AxiosResponse<{
      existing: string[]
    }> = await this.post(`${FILE_MANAGER}/checkExists/`, { target, names })
    return this.success(res).existing
  }

  /**
   *
   * @param {string} source -Location of the file or folder to rename.
   * @param {string} newName -New name of the folder or file.
   * @description Rename a file or folder.
   */
  public async renameFileOrFolder(source: string, newName: string): Promise<{ path: string }> {
    try {
      const data = { source: source, newName: newName }
      const res: AxiosResponse<{ path: string }> = await this.post(
        `${FILE_MANAGER}/renameFile/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getMainFolders(): Promise<MainFolder[]> {
    const res: AxiosResponse<MainFolder[]> = await this.get(`${FILE_MANAGER}/getMainFolders/`)
    return this.success(res)
  }

  /**
   *
   * @param {string} fullPath -Location of the folder.
   * @description Get children of a folder. The backend now paginates getFiles;
   * this pages through and returns the full list so existing callers (tree /
   * navigation) keep their MainFolder[] contract. New incremental consumers
   * should use getFolderPage instead.
   */
  public async getFoldersAndFiles(fullPath: string): Promise<MainFolder[]> {
    return this.fetchAllPages({ folder: fullPath, apply_filters: true })
  }

  public async copyFiles(params: CopyOrMoveData): Promise<FileOperationResponse> {
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/copyFiles/`,
        {
          params,
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async copyAppToWorkspace(params: CopyAppToWorkspaceData): Promise<FileOperationResponse> {
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/copyAppToWorkspace/`,
        { params }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async copyAppToTeam(params: CopyAppToTeamData): Promise<FileOperationResponse> {
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/copyAppToTeam/`,
        { params }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async copyAppToPublic(params: CopyAppToWorkspaceData): Promise<FileOperationResponse> {
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/copyAppToPublic/`,
        { params }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   *
   * @param {string[]} sources -Location of files or folders to delete.
   * @description Delete files or folders.
   */
  public async deleteFilesOrFolder(sources: string[]): Promise<void | FileOperationResponse> {
    const data = { sources: sources }
    try {
      const res: AxiosResponse<void | FileOperationResponse> = await this.delete(
        `${FILE_MANAGER}/deleteFiles/`,
        {
          data: data
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async downloadFileOrFolder(
    params: DownloadParams,
    progressEvent?: (progressEvent: ProgressEvent) => void
  ): Promise<any> {
    try {
      const res: AxiosResponse<any> = await this.get(`${FILE_MANAGER}/download/`, {
        responseType: 'blob',
        params,
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
        onDownloadProgress(progress) {
          progressEvent?.(progress)
        }
      })
      return { data: this.success<any>(res), headers: res.headers }
    } catch (error) {
      throw error
    }
  }

  public async prepareDownloadFileOrFolder(params: DownloadParams): Promise<FileOperationResponse> {
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/download/`,
        {
          params: {
            ...params,
            async: true
          },
          paramsSerializer: (downloadParams) =>
            qs.stringify(downloadParams, { arrayFormat: 'repeat' })
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public getPreparedDownloadFileOrFolderUrl(operationId: string): string {
    return this.buildBrowserDownloadUrl(
      new URLSearchParams({
        operationId
      })
    )
  }

  public getDirectDownloadFileOrFolderUrl(params: DownloadParams): string {
    const searchParams = new URLSearchParams()

    params.sources.forEach((source) => {
      searchParams.append('sources', source)
    })

    return this.buildBrowserDownloadUrl(searchParams)
  }

  /**
   *
   * @param {string[]} sources -Location of files to zip.
   * @description Zip a file.
   */
  public async zipFileOrFolder({ sources }: ZipParams): Promise<FileOperationResponse> {
    const params = { sources }
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/zipFiles/`,
        {
          params,
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   *
   * @param {string} source -Location of file to unzip.
   * @param {string} targetFolder -Location where unzip the files.
   * @description Unzip a file.
   */
  public async unZipFile(
    source: string,
    targetFolder: string,
    deleteFile?: boolean
  ): Promise<void> {
    try {
      const query = `?source=${encodeURIComponent(source)}&targetFolder=${encodeURIComponent(
        targetFolder
      )}&deleteFile=${deleteFile ? deleteFile : ''}`
      const res: AxiosResponse<void> = await this.get(`${FILE_MANAGER}/unzipFile/${query}`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   *
   * @param {string[]} sources -Location of files to duplicate.
   * @description Duplicate files.
   */
  public async duplicateFile({ sources }: ZipParams): Promise<FileOperationResponse> {
    const params = { sources }
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/duplicateFiles/`,
        {
          params,
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   *
   * @param {string} folder_path -The location for the new folder.
   * @param {string} folder_name -The name of the new folder.
   * @description Create a new folder.
   */
  public async createFolder({
    folder_path,
    folder_name
  }: {
    folder_path: string
    folder_name: string
  }): Promise<{ path: string }> {
    const res: AxiosResponse<{ path: string }> = await this.post(`${FILE_MANAGER}/createFolder/`, {
      folder_path,
      folder_name
    })
    return this.success(res)
  }
  /**
   *
   * @param {string} path - The location where the new file will be created.
   * @param {string} file_name - The name of the new file.
   * @description Create a new file.
   */
  public async createFile({
    folder_path,
    file_name
  }: {
    folder_path: string
    file_name: string
  }): Promise<{ path: string }> {
    const res: AxiosResponse<{ path: string }> = await this.post(`${FILE_MANAGER}/createFile/`, {
      folder_path,
      file_name
    })
    return this.success(res)
  }

  /**
   *
   * @param {Object} data -File chunks,file name and location.
   * @description Upload a file.
   */
  public async uploadFile(data: FormData, token: CancelToken): Promise<{ path: string }> {
    const res: AxiosResponse<{ path: string }> = await this.post(`${FILE_MANAGER}/upload/`, data, {
      cancelToken: token
    })
    return this.success(res)
  }

  /**
   *
   * @param {string[]} sources -Location of files to move.
   * @param {string} target -New location of the files.
   * @description Move files.
   */
  public async moveFile({ sources, target }: CopyOrMoveData): Promise<FileOperationResponse> {
    const params = { sources, target }
    try {
      const res: AxiosResponse<FileOperationResponse> = await this.get(
        `${FILE_MANAGER}/moveFiles/`,
        {
          params,
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   *
   * @param {string} operationId - The ID of the async operation to check.
   * @description Get the status of an async file operation (copy/move).
   */
  public async getAsyncOperationStatus(operationId: string): Promise<AsyncOperationStatus> {
    try {
      const res: AxiosResponse<AsyncOperationStatus> = await this.get(
        `${FILE_MANAGER}/getAsyncOperationStatus/`,
        {
          params: { operationId }
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async modifyFileContent(path: string, content: string): Promise<any> {
    try {
      const res: AxiosResponse<any> = await this.post(`${FILE_MANAGER}/modifyFileContent/`, {
        path,
        content
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async completeUpload(uploadParams: UploaderParams): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.post(
        `${FILE_MANAGER}/completeUpload/`,
        uploadParams
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async optimizeTemplates(params: DownloadParams): Promise<{ optimized: boolean }> {
    try {
      const res: AxiosResponse<{ optimized: boolean }> = await this.get(
        `${FILE_MANAGER}/optimizeTemplates/`,
        {
          params,
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
}

export const fileManagerApi = new FileManagerApi(apiConfig)
